import React from 'react';
import type { EggPhase } from '../types';

export interface MarketAsset {
  symbol: string;
  name: string;
  subCode: string;
  close: number;
  changeValue: number;
  changePct: number;
  bias200Pct: number;
  vr60: number;
  cpr: number;
  phase: EggPhase;
  phaseLabel: string;
  phaseBadgeColor?: string;
  sparkBars: number[]; // relative percentages for volume bars
}

interface MarketCardsProps {
  assets?: MarketAsset[];
}

const DEFAULT_ASSETS: MarketAsset[] = [
  {
    symbol: '^TWII',
    name: '加權指數',
    subCode: 'TAIEX',
    close: 46969.87,
    changeValue: -1655.96,
    changePct: -3.41,
    bias200Pct: 17.5,
    vr60: 105,
    cpr: 0.11,
    phase: 'A2',
    phaseLabel: 'A2 升市中段',
    sparkBars: [85, 78, 62, 55, 42],
  },
  {
    symbol: '^TWOII',
    name: '櫃買指數',
    subCode: 'TPEx',
    close: 396.90,
    changeValue: -23.23,
    changePct: -5.53,
    bias200Pct: 12.4,
    vr60: 115,
    cpr: 0.08,
    phase: 'A2',
    phaseLabel: 'A2 升市中段',
    sparkBars: [80, 72, 60, 48, 38],
  },
  {
    symbol: '^MSCITW',
    name: 'MSCI Taiwan Index',
    subCode: 'MSCI-TW',
    close: 2128.45,
    changeValue: -78.60,
    changePct: -3.56,
    bias200Pct: 18.2,
    vr60: 118,
    cpr: 0.15,
    phase: 'A2',
    phaseLabel: 'A2 升市中段',
    sparkBars: [88, 80, 68, 56, 45],
  },
  {
    symbol: '0050.TW',
    name: '元大台灣50',
    subCode: '0050',
    close: 108.90,
    changeValue: -4.10,
    changePct: -3.63,
    bias200Pct: 18.3,
    vr60: 104,
    cpr: 0.12,
    phase: 'A2',
    phaseLabel: 'A2 升市中段',
    sparkBars: [82, 75, 64, 52, 40],
  },
  {
    symbol: '00878.TW',
    name: '國泰永續高股息',
    subCode: '00878',
    close: 35.12,
    changeValue: -0.35,
    changePct: -0.99,
    bias200Pct: 24.1,
    vr60: 72,
    cpr: 0.32,
    phase: 'A1',
    phaseLabel: 'A1 升市初段',
    sparkBars: [60, 58, 52, 48, 45],
  },
  {
    symbol: 'TX.TF',
    name: '台指期 TX 近月',
    subCode: 'TX10',
    close: 46979.00,
    changeValue: -1819.00,
    changePct: -3.73,
    bias200Pct: 17.5,
    vr60: 112,
    cpr: 0.11,
    phase: 'A2',
    phaseLabel: 'A2 升市中段',
    sparkBars: [88, 82, 65, 54, 44],
  },
];

export const MarketCards: React.FC<MarketCardsProps> = ({ assets = DEFAULT_ASSETS }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {assets.map((asset) => {
        const isUp = asset.changePct >= 0;
        const badgeClass =
          asset.phaseBadgeColor ||
          (asset.phase === 'A2'
            ? 'bg-blue-950/65 text-terminal-cyan border-blue-500/40'
            : asset.phase === 'A1'
            ? 'bg-indigo-950/55 text-sky-300 border-indigo-500/35'
            : 'bg-[#161b26] text-slate-300 border-[#263248]');

        return (
          <div
            key={asset.symbol}
            data-testid={`market-card-${asset.symbol}`}
            className="p-3.5 rounded-lg stitch-card flex flex-col justify-between hover:border-blue-500/50 transition-colors"
          >
            {/* Top Row: Name, Subcode, Phase Badge */}
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm font-semibold text-slate-100">{asset.name}</span>
                <span className="text-[11px] font-mono font-semibold text-sky-400/90">{asset.subCode}</span>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold border ${badgeClass}`}>
                {asset.phaseLabel}
              </span>
            </div>

            {/* Mid Row: Price and Change */}
            <div className="my-2 flex items-baseline justify-between font-mono">
              <span className="text-lg md:text-xl font-bold text-slate-100">
                {asset.close >= 1000
                  ? asset.close.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : asset.close.toFixed(2)}
              </span>
              <span className={`text-xs font-bold ${isUp ? 'text-market-up' : 'text-market-down'}`}>
                {isUp ? '+' : ''}
                {asset.changeValue.toFixed(2)} ({isUp ? '+' : ''}
                {asset.changePct.toFixed(2)}%)
              </span>
            </div>

            {/* Volume Trend Micro-bars matching Stitch */}
            <div className="h-6 w-full flex items-end gap-1 py-0.5 mb-2">
              {asset.sparkBars.map((h, idx) => {
                let barBg = 'bg-[#262f40]';
                if (idx >= 2 && idx < 4) barBg = 'bg-blue-500/50';
                if (idx === 4) barBg = asset.phase === 'A3' ? 'bg-amber-400' : 'bg-terminal-cyan';

                return (
                  <div
                    key={idx}
                    className={`w-1.5 rounded-xs transition-all ${barBg}`}
                    style={{ height: `${h}%` }}
                  />
                );
              })}
              <div className="flex-1 flex justify-end text-[11px] text-sky-300/80 font-mono">
                VR: {asset.vr60}%
              </div>
            </div>

            {/* Bottom Metrics in Soft Recessed Pill Bar (Zero Harsh Lines) */}
            <div className="px-2.5 py-1.5 rounded-md bg-[#0b0f17]/80 grid grid-cols-3 text-[11px] font-mono text-slate-300">
              <span>
                Bias:{' '}
                <strong className={asset.bias200Pct >= 15 ? 'text-amber-400' : 'text-terminal-cyan'}>
                  +{asset.bias200Pct}%
                </strong>
              </span>
              <span className="text-center">
                VR60: <strong className="text-slate-100">{asset.vr60}%</strong>
              </span>
              <span className="text-right">
                CPR: <strong className="text-slate-100">{asset.cpr}</strong>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
